import { Menu, Transition } from '@headlessui/react';
import { cva } from 'class-variance-authority';
import { compact } from 'lodash';
import { FC, Fragment, ReactNode, SelectHTMLAttributes, useMemo, useState } from 'react';
import { List } from 'react-virtualized';
import { cn } from '../../../utils/helpers';

type Value = string | number | boolean;

interface OPTIONS_PROPS {
  label: string;
  value: Value;
  isSelected?: boolean;
  disabled?: boolean;
  iconLeft?: ReactNode;
  iconRight?: ReactNode;
  className?: string;
  onClick?: () => void;
}

interface PROPS extends Omit<SelectHTMLAttributes<HTMLSelectElement>, 'onChange' | 'value'> {
  label: string;
  options: OPTIONS_PROPS[];
  onChange: (value: Value) => void;
  value?: Value | Value[];
  virtualized?: boolean;
  labelClassname?: string;
  buttonClassname?: string;
  error?: string;
  isMultiSelect?: boolean;
}

const dropdownButtonClasses = cva(
  ['flex items-center justify-between', 'bg-fill-primary rounded', 'border', 'p-4 w-full h-[52px]'],
  {
    variants: {
      size: {},
      isOpen: {
        true: [],
        false: [],
      },
    },
    defaultVariants: {
      isOpen: false,
    },
  },
);

const Dropdown: FC<PROPS> = ({
  label,
  className,
  options,
  disabled,
  name,
  value,
  onChange,
  virtualized,
  labelClassname,
  buttonClassname,
  error,
  isMultiSelect,
}) => {
  const [selected, setSelected] = useState<typeof value | (typeof value)[]>(value);

  const displayText = useMemo(() => {
    if (isMultiSelect) {
      const filteredOptions = options?.filter((opt) => (selected as Value[])?.includes(opt.value));

      return filteredOptions.map((opt) => opt.label).join(', ');
    } else {
      return options.find((opt) => opt.value === selected)?.label ?? selected;
    }
  }, [isMultiSelect, options, selected]);

  const handleChange = (value: Value) => {
    if (isMultiSelect) {
      let _selected = [...(selected as Value[])];

      if (!_selected.includes(value)) {
        _selected.push(value);
      } else {
        _selected = _selected.filter((item) => item !== value);
      }

      setSelected(_selected);
    } else {
      setSelected(value);
    }

    onChange(value);
  };

  const upLabel = useMemo(() => {
    if (typeof selected === 'string' || typeof selected === 'number' || typeof selected === 'undefined') {
      return !selected && selected?.toString() !== 'false' && selected?.toString() !== '0';
    }

    if (typeof selected === 'boolean') {
      return false;
    }

    return compact(selected as Value[])?.length === 0;
  }, [selected]);

  return (
    <div className={cn('relative w-full', error && 'mb-2', className)}>
      <label
        htmlFor={name}
        className={cn(
          'absolute',
          'transition-all duration-100',
          upLabel
            ? 'top-1/2 transform -translate-y-1/2 text-sm left-4 text-content'
            : 'top-[-7px] bg-white text-xs px-1 left-3 z-10 text-raisin-black',
          labelClassname,
        )}
      >
        {label}
      </label>
      <Menu as="div" className="relative inline-block text-left w-full">
        {({ open: isOpen }) => (
          <>
            <Menu.Button
              disabled={disabled}
              className={cn(
                dropdownButtonClasses({ isOpen }),
                error ? 'border-negative' : 'border-[#DADADA]',
                buttonClassname,
              )}
              type="button"
            >
              <span className="text-sm truncate">{displayText}</span>
              <span>
                <svg xmlns="http://www.w3.org/2000/svg" width="12" height="8" viewBox="0 0 12 8" fill="none">
                  <path
                    d="M1 1.5L6 6.5L11 1.5"
                    stroke="#979797"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                </svg>
              </span>
            </Menu.Button>
            <Transition
              as={Fragment}
              enter="transition ease-out duration-100"
              enterFrom="transform opacity-0 scale-95"
              enterTo="transform opacity-100 scale-100"
              leave="transition ease-in duration-75"
              leaveFrom="transform opacity-100 scale-100"
              leaveTo="transform opacity-0 scale-95"
            >
              {!virtualized ? (
                <Menu.Items
                  className={cn(
                    'w-full z-20 mt-1',
                    'absolute right-0 origin-top-right',
                    'shadow-sm',
                    'max-h-[280px] overflow-y-auto',
                    'rounded bg-[#F7F7F7]',
                    'border border-[#DADADA]',
                  )}
                >
                  {options.map((option, index) => (
                    <Menu.Item key={index}>
                      <button
                        type="button"
                        onClick={() => {
                          handleChange(option.value);
                        }}
                        className={cn(
                          'px-6 py-4 w-full text-left',
                          'text-sm',
                          'hover:bg-positive',
                          'flex items-center gap-x-2.5',
                          option.className,
                          (isMultiSelect ? (selected as Value[])?.includes(option.value) : selected === option.value) &&
                            'bg-positive',
                        )}
                      >
                        {option.iconLeft}
                        <span className="max-w-4/5 truncate">{option.label}</span>
                        {option.iconRight}
                      </button>
                    </Menu.Item>
                  ))}
                </Menu.Items>
              ) : (
                <Menu.Items
                  className={cn(
                    'w-full z-20 mt-1',
                    'absolute right-0 origin-top-right',
                    'shadow-sm',
                    'rounded bg-[#F7F7F7]',
                    'border border-[#DADADA]',
                  )}
                >
                  <List
                    height={270}
                    width={209.37}
                    rowHeight={60} // Adjust the row height as needed
                    rowCount={options.length}
                    rowRenderer={({ key, index, style }) => (
                      <div key={key} style={style}>
                        <Menu.Item>
                          <button
                            type="button"
                            onClick={() => {
                              handleChange(options[index].value);
                            }}
                            className={cn(
                              'px-6 py-4 w-full text-left',
                              'text-sm',
                              'hover:bg-positive',
                              'flex items-center gap-x-2.5',
                              options[index].className,
                              (isMultiSelect
                                ? (selected as Value[]).includes(options[index].value)
                                : selected === options[index].value) && 'bg-positive',
                            )}
                          >
                            {options[index].iconLeft}
                            <span className="max-w-4/5 truncate">{options[index].label}</span>
                            {options[index].iconRight}
                          </button>
                        </Menu.Item>
                      </div>
                    )}
                  />
                </Menu.Items>
              )}
            </Transition>
          </>
        )}
      </Menu>
      {error && <p className={cn('text-sm text-negative', 'absolute bottom-[-20px]')}>{error}</p>}
    </div>
  );
};

export default Dropdown;
